// src/components/shared/AppIconRenderer.jsx
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { HelpCircle as DefaultFallbackIcon } from 'lucide-react';

// --- For Local/Mapped Icons (includes your SVGs and explicitly chosen Phosphor icons) ---
import { localIcons } from '../../utility/localIconMap'; // Adjust path to your map file

// --- For Lucide Icons (Tree-shakeable & Recommended) ---
import lucideIconImporters from 'lucide-react/dynamicIconImports';

// Helper to convert input to PascalCase (for localIcons map keys)
const toPascalCase = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .toLowerCase()
    .split(/[\s_-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

// Helper to convert input to kebab-case (for Lucide importers keys)
const toKebabCase = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
};

const AppIconRenderer = ({ name, fallbackIcon: FallbackComponent = DefaultFallbackIcon, ...props }) => {
  const [LoadedIconComponent, setLoadedIconComponent] = useState(null);
  const [isLazyLoaded, setIsLazyLoaded] = useState(false);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    let isMounted = true;
    if (!name) {
      setStatus('idle'); setLoadedIconComponent(null); setIsLazyLoaded(false);
      return;
    }

    setStatus('loading'); setLoadedIconComponent(null); setIsLazyLoaded(false);

    const attemptLoad = async () => {
      // 1. Try Local/Mapped Icons first
      const iconNamePascal = toPascalCase(name);
      // console.log(`[AppIconRenderer] Input: '${name}', Trying local/mapped with: '${iconNamePascal}'`);
      if (localIcons.hasOwnProperty(iconNamePascal)) {
        // console.log(`[AppIconRenderer] Found '${iconNamePascal}' in localIcons map.`);
        if (isMounted) {
          setLoadedIconComponent(() => localIcons[iconNamePascal]);
          setIsLazyLoaded(false); // These are directly available, not lazy
          setStatus('success');
        }
        return;
      }
      // console.log(`[AppIconRenderer] '${iconNamePascal}' not in localIcons map. Trying Lucide...`);

      // 2. Try Lucide Icons if not found in the local map
      const lucideNameKebab = toKebabCase(name);
      const lucideImporterFn = lucideIconImporters[lucideNameKebab];
      if (lucideImporterFn) {
        // console.log(`[AppIconRenderer] Found '${lucideNameKebab}' in Lucide. Lazy loading...`);
        try {
          const LazyLucide = lazy(lucideImporterFn);
          if (isMounted) {
            setLoadedIconComponent(() => LazyLucide);
            setIsLazyLoaded(true); // Lucide icons are lazy-loaded
            setStatus('success');
          }
        } catch (err) {
          console.error(`[AppIconRenderer] Error lazy-loading Lucide icon '${lucideNameKebab}':`, err);
          if (isMounted) setStatus('error');
        }
        return;
      }

      // console.log(`[AppIconRenderer] Icon '${name}' not found.`);
      if (isMounted) setStatus('error');
    };

    attemptLoad();
    return () => { isMounted = false; };
  }, [name]);

  const loadingFallback = (
    <div 
      style={{ width: props.size || 20, height: props.size || 20 }} 
      className="bg-muted rounded animate-pulse" 
      aria-label="Loading icon..." 
    />
  );

  if (status === 'loading') {
      return loadingFallback;
  }

  if (status === 'success' && LoadedIconComponent) {
    if (isLazyLoaded) { // Lucide icons need Suspense
      return (
        <Suspense fallback={loadingFallback}>
          <LoadedIconComponent {...props} />
        </Suspense>
      );
    }
    // Local/mapped icons (SVGs as components, directly imported Phosphor icons)
    return <LoadedIconComponent {...props} />; 
  }

  if (status === 'error' || (status === 'idle' && name)) {
    return <FallbackComponent {...props} title={`Icon not found: ${name}`} />;
  }
  return null;
};

export default AppIconRenderer;